import {
    AreaSubscriptionSummary,
    AreaSubscriptionSummaryTranslation,
    ExtendedAreaSubscriptionState,
} from '@features/area-subscription/types';
import { getTranslationOptions } from '@features/area-subscription/utilities';
import { translateItems } from '@utilities/hooks';

const summaryTranslationKeys: AreaSubscriptionSummaryTranslation = {
    confirmationTitle: 'AreaSubscription.Modal.Confirmation.Title',
    confirmationAreaPositive: 'AreaSubscription.Modal.Confirmation.AreaSubscribed.Positive',
    confirmationProjectsPositive: 'AreaSubscription.Modal.Confirmation.ProjectsSubscribed.Positive',
    confirmationProjectsNegative: 'AreaSubscription.Modal.Confirmation.ProjectsSubscribed.Negative',
    confirmationPropertiesPositive:
        'AreaSubscription.Modal.Confirmation.PropertiesSubscribed.Positive',
    confirmationAfterTextAreaSubscribedNoProjects: '',
    step3AdvantagesTitle: 'AreaSubscription.Modal.Step3.ExtraText.Title',
    step3AdvantagesLine1: 'AreaSubscription.Modal.Step3.ExtraText.Line1',
    step3AdvantagesLine2: 'AreaSubscription.Modal.Step3.ExtraText.Line2',
    step3AdvantagesLine3: 'AreaSubscription.Modal.Step3.ExtraText.Line3',
    step3AdvantagesLine4: 'AreaSubscription.Modal.Step3.ExtraText.Line4',
    step4AdvantagesTitle: 'AreaSubscription.Modal.Step4.ExtraText.Title',
    step4AdvantagesLine1: 'AreaSubscription.Modal.Step4.ExtraText.Line1',
    step4AdvantagesLine2: 'AreaSubscription.Modal.Step4.ExtraText.Line2',
    step4AdvantagesLine3: 'AreaSubscription.Modal.Step4.ExtraText.Line3',
    step4AdvantagesLine4: 'AreaSubscription.Modal.Step4.ExtraText.Line4',
};

const getSummaryData = async (
    state: ExtendedAreaSubscriptionState,
    options: { step: number },
): Promise<AreaSubscriptionSummary> => {
    const translationOptions = getTranslationOptions();
    const translatedSummaryData = await translateItems(summaryTranslationKeys, translationOptions);

    const { confirmationTitle, confirmationAreaPositive } = translatedSummaryData;
    const data = {
        confirmation: {
            title: confirmationTitle!,
            items: [{ value: confirmationAreaPositive!, positive: true }],
        },
    } as AreaSubscriptionSummary;

    switch (options.step) {
        case 2: {
            switch (state.status) {
                case 'subscribedToAreaNoProjectsAvailable':
                case 'subscribedToAreaNotSubscribedToProjects': {
                    if (state.status === 'subscribedToAreaNotSubscribedToProjects') {
                        data.confirmation.items.push({
                            value: translatedSummaryData.confirmationProjectsNegative!,
                            positive: false,
                        });
                        data.confirmation.afterText =
                            translatedSummaryData.confirmationAfterTextAreaSubscribedNoProjects;
                    }
                    data.advantages = {
                        title: translatedSummaryData.step3AdvantagesTitle!,
                        items: [
                            { value: translatedSummaryData.step3AdvantagesLine1!, positive: true },
                            { value: translatedSummaryData.step3AdvantagesLine2!, positive: true },
                            { value: translatedSummaryData.step3AdvantagesLine3!, positive: true },
                            { value: translatedSummaryData.step3AdvantagesLine4!, positive: true },
                        ],
                    };
                    break;
                }
                case 'subscribedToAreaSubscribedToSomeProjects':
                case 'subscribedToAreaSubscribedToAllProjects': {
                    data.confirmation.items.push({
                        value: translatedSummaryData.confirmationProjectsPositive!,
                        positive: true,
                    });
                    break;
                }
            }
            break;
        }
        case 3: {
            data.confirmation.items.push({
                value: translatedSummaryData.confirmationProjectsPositive!,
                positive: true,
            });
            data.confirmation.items.push({
                value: translatedSummaryData.confirmationPropertiesPositive!,
                positive: true,
            });
            data.advantages = {
                title: translatedSummaryData.step4AdvantagesTitle!,
                items: [
                    { value: translatedSummaryData.step4AdvantagesLine1!, positive: true },
                    { value: translatedSummaryData.step4AdvantagesLine2!, positive: true },
                    { value: translatedSummaryData.step4AdvantagesLine3!, positive: true },
                    { value: translatedSummaryData.step4AdvantagesLine4!, positive: true },
                ],
            };
            break;
        }
    }

    return data;
};

export { getSummaryData };
